import type {
  BackendExperiment,
  StoredExperimentData,
} from '@core/abTesting/backend/types'
import { logInfo } from '@core/logging/helpers'
import { getCookie } from '@core/storage'
import { StorageKeyPerformanceEnum } from '@core/storage/types'
import { removeCookieAndFallback } from '@core/tracking/funnelTracking'
import { setCookieWithRootDomain } from '@core/tracking/funnelTracking/cookies/setCookieWithRootDomain'

/**
 * Cookie expiration time in days
 */
const COOKIE_EXPIRATION_DAYS = 30

/**
 * Cache duration in milliseconds (10 minutes)
 */
const CACHE_DURATION_MS = 10 * 60 * 1000

/**
 * Sets experiments data in the fw_abt cookie
 */
export async function setExperimentsCookie(
  experiments: BackendExperiment[],
): Promise<void> {
  const { default: add } = await import('date-fns/add')

  const data: StoredExperimentData = {
    experiments,
    timestamp: Date.now(),
  }

  setCookieWithRootDomain(
    StorageKeyPerformanceEnum.fw_abt,
    JSON.stringify(data),
    {
      expirationDate: add(new Date(), { days: COOKIE_EXPIRATION_DAYS }),
    },
  )

  logInfo(`Backend A/B Tests stored: ${experiments.length} experiments`)
}

/**
 * Gets experiments data from the fw_abt cookie
 */
export function getStoredExperiments(): BackendExperiment[] | null {
  const cookie = getCookie(StorageKeyPerformanceEnum.fw_abt)

  if (!cookie) {
    return null
  }

  try {
    const data: StoredExperimentData = JSON.parse(cookie)

    // Check if data has the new backend format
    if (!data.experiments || !Array.isArray(data.experiments)) {
      // Legacy format or invalid data
      return null
    }

    return data.experiments
  } catch (error) {
    logInfo('Failed to parse experiments cookie, will refetch')
    return null
  }
}

/**
 * Checks if stored experiments are still valid (not expired)
 */
export function areStoredExperimentsValid(): boolean {
  const cookie = getCookie(StorageKeyPerformanceEnum.fw_abt)

  if (!cookie) {
    return false
  }

  try {
    const data: StoredExperimentData = JSON.parse(cookie)

    if (!data.timestamp) {
      // Legacy format without timestamp
      return false
    }

    const age = Date.now() - data.timestamp
    return age < CACHE_DURATION_MS
  } catch (error) {
    return false
  }
}

/**
 * Clears the experiments cookie
 */
export function clearExperimentsCookie(): void {
  removeCookieAndFallback(StorageKeyPerformanceEnum.fw_abt)
  logInfo('Backend A/B Tests cookie cleared')
}
